import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import clsx from "clsx";
import { SearchAndFilters } from "../../../Components/SearchAndFilters/SearchAndFilters";
import { Layout } from "../../../Components/Layout/Layout";
import { Footer } from "../../../Components/Footer/Footer";
import { Hero } from "Components/Hero/Hero";
import { CourseCard } from "../../../Components/CourseCard/CourseCard";
import { Tabs } from "Components/Tabs/Tabs";
import { Badge } from "Components/Badge/Badge";
import { Pagination } from "Components/Pagination/Pagination";
import { useCategorias } from "Hooks/useCategorias";
import { useDragScroll } from "Hooks/useDragScroll";
import { useMobile } from "Hooks/useMobile";
import { useCursosPaquetes } from "Hooks/useCursosPaquetes";
import { useFavorite } from "Hooks/useFavorite";
import { useDispatch } from "react-redux";
import { setSelectedFilters } from "Slices/uiSlice";
import { DynamicIcon } from "Components/DynamicIcon/DynamicIcon";
import { CourseCardSkeleton } from "Components/CourseCardSkeleton/CourseCardSkeleton";
import { useNavigate } from "react-router-dom";
import { RUTAS } from "rutas";
import "./Landing.scss";

const TABS = [{ label: "Cursos" }, { label: "Paquete de Cursos" }];

const passesFilter = (selectedFilter, itemValue) =>
  selectedFilter.length === 0 || selectedFilter.includes(itemValue);

export const Landing = () => {
  const dispatch = useDispatch();
  const { authUser } = useSelector((state) => state.auth);
  const savedFilters = useSelector((state) => state.ui.selectedFilters);
  const isAuthenticated = Boolean(authUser.token);
  const idUsuario = authUser.idUsuario;
  const isMobile = useMobile();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { categorias } = useCategorias();
  const { cursos, paquetes, isLoading } = useCursosPaquetes();
  const { dragProps } = useDragScroll({ horizontal: true, vertical: false });
  const { toggleFavorite, isItemProcessing } = useFavorite(idUsuario);
  const itemsPerPage = isMobile ? 10 : 9;
  const currentData = savedFilters.tipos[0] === 1 ? cursos : paquetes;
  const navigate = useNavigate();

  const filtersKey = useMemo(() => {
    return JSON.stringify({
      tipos: savedFilters.tipos,
      temas: savedFilters.temas,
      idiomas: savedFilters.idiomas,
      dificultad: savedFilters.dificultad,
      selectedCategory,
    });
  }, [savedFilters, selectedCategory]);

  const prevFiltersKey = useRef(filtersKey);

  const filteredData = useMemo(
    () =>
      currentData.filter(
        (data) =>
          (!selectedCategory || selectedCategory === data.idCategoria) &&
          passesFilter(savedFilters.temas, data.idSubcategoria) &&
          passesFilter(savedFilters.idiomas, data.idIdioma) &&
          passesFilter(savedFilters.dificultad, data.idDificultad)
      ),
    [currentData, savedFilters, selectedCategory]
  );

  useEffect(() => {
    if (prevFiltersKey.current !== filtersKey) {
      setCurrentPage(1);
      prevFiltersKey.current = filtersKey;
    }
  }, [filtersKey]);

  const { paginatedData, totalPages } = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const slicedData = filteredData.slice(startIndex, endIndex);
    const total = Math.ceil(filteredData.length / itemsPerPage);

    return {
      paginatedData: slicedData,
      totalPages: total,
    };
  }, [filteredData, currentPage, itemsPerPage]);

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory((prev) => (prev === categoryId ? null : categoryId));
  };

  const handleTabChange = (tab) => {
    dispatch(
      setSelectedFilters({
        ...savedFilters,
        tipos: [tab],
      })
    );
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handleCardClick = (learningItem) => {
    const { idCurso, idPaquete } = learningItem;

    const path = idCurso ? RUTAS.CURSO(idCurso) : RUTAS.PAQUETE(idPaquete);
    navigate(path);
  };

  return (
    <Layout>
      <div
        className={clsx("main-content", {
          "main-content__authenticated": isAuthenticated,
        })}
      >
        {!isAuthenticated && <Hero />}

        <div
          className="main-content__tabs-categories-wrapper"
          id="tabs-categories"
        >
          <Tabs
            items={TABS}
            activeTab={savedFilters.tipos[0]}
            onChange={handleTabChange}
            className="tab"
          />

          <div>
            <p className="main-content__about-cursos-paquetes">
              Encuentra cursos acerca de:
            </p>
            <div className="main-content__categories" {...dragProps}>
              {categorias.map((categoria) => (
                <Badge
                  key={categoria.idCategoria}
                  id={`categoria-${categoria.idCategoria}`}
                  textColor={categoria.colorTexto}
                  backgroundColor={categoria.colorFondo}
                  borderColor={categoria.colorBorde}
                  variant="filter"
                  onClick={() => handleCategoryClick(categoria.idCategoria)}
                  className="category-badge"
                  selected={selectedCategory === categoria.idCategoria}
                >
                  <DynamicIcon
                    iconName={categoria.icono}
                    color={categoria.colorTexto}
                  />
                  {categoria.nombre}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <div className="main-content__container">
          {!isLoading && paginatedData.length === 0 && (
            <div className="main-content__no-results">
              No se encontraron&nbsp;
              <strong>
                {savedFilters.tipos[0] === 1 ? "Cursos" : "Paquetes de Cursos"}
              </strong>
              <br />
              Prueba ajustando los filtros o explorando otras categorías
            </div>
          )}

          <div className="main-content__grid">
            {!isLoading &&
              paginatedData.map((learningItem) => {
                const isProcessing = isItemProcessing(learningItem.idAlterno);

                return (
                  <CourseCard
                    key={learningItem.idAlterno}
                    title={learningItem.titulo}
                    imageUrl={learningItem.img}
                    duration={learningItem.duracion}
                    difficulty={learningItem.dificultad}
                    language={learningItem.idioma}
                    badgeText={learningItem.categoria}
                    badgeTextColor={learningItem.colorTexto}
                    badgeBackgroundColor={learningItem.colorFondo}
                    badgeBorderColor={learningItem.colorBorde}
                    isFavorite={Boolean(learningItem.favorito)}
                    variant="default"
                    isLoggedIn={isAuthenticated}
                    onToggleFavorite={() => toggleFavorite(learningItem)}
                    onClick={() => handleCardClick(learningItem)}
                    isProcessingFavorite={isProcessing}
                  />
                );
              })}

            {isLoading &&
              Array.from({ length: itemsPerPage }).map((_, index) => (
                <CourseCardSkeleton key={index} variant="default" />
              ))}
          </div>

          {paginatedData.length !== 0 && (
            <div className="main-content__pagination-container">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
      <SearchAndFilters />
      <Footer />
    </Layout>
  );
};
